import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, FileText, Calendar, Wrench, CheckCircle, HeadphonesIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { processSteps } from '../mock';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const HowItWorks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  // Translated process steps
  const translatedSteps = [
    {
      id: 1,
      title: t('processSteps.step1Title'),
      description: t('processSteps.step1Desc'),
      icon: 'MessageSquare'
    },
    {
      id: 2,
      title: t('processSteps.step2Title'),
      description: t('processSteps.step2Desc'),
      icon: 'FileText'
    },
    {
      id: 3,
      title: t('processSteps.step3Title'),
      description: t('processSteps.step3Desc'),
      icon: 'Calendar'
    },
    {
      id: 4,
      title: t('processSteps.step4Title'),
      description: t('processSteps.step4Desc'),
      icon: 'Wrench'
    },
    {
      id: 5,
      title: t('processSteps.step5Title'),
      description: t('processSteps.step5Desc'),
      icon: 'CheckCircle'
    },
    {
      id: 6,
      title: t('processSteps.step6Title'),
      description: t('processSteps.step6Desc'),
      icon: 'HeadphonesIcon'
    }
  ];
  const iconMap = {
    'MessageSquare': MessageSquare,
    'FileText': FileText,
    'Calendar': Calendar,
    'Wrench': Wrench,
    'CheckCircle': CheckCircle,
    'HeadphonesIcon': HeadphonesIcon
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-[#1a1f2e]">
      <SEO 
        title={`${t('howItWorks.title')} - DumaExpertBéton`}
        description={t('howItWorks.intro1')}
        url="https://dumaexpertbeton.fr/comment-ca-marche"
      />
      
      {/* Header */}
      <div className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="flex justify-between items-start mb-4">
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all duration-200 cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">{t('common.back')}</span>
            </button>
            <LanguageSwitcher />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-4">{t('howItWorks.title')}</h1>
          <p className="text-xl text-slate-300 mt-4">{t('howItWorks.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-xl text-slate-300 leading-relaxed mb-6">
              {t('howItWorks.intro1')}
            </p>
            <p className="text-lg text-slate-400 leading-relaxed">
              {t('howItWorks.intro2')}
            </p>
          </section>

          {/* Process Steps */}
          <section className="space-y-8">
            {translatedSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <Card key={step.id} className="overflow-hidden bg-slate-900 border-2 border-slate-700 hover:border-teal-600 transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                          {step.id}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          {IconComponent && <IconComponent className="w-6 h-6 text-teal-400" />}
                          <h3 className="text-2xl font-bold text-white">{step.title}</h3>
                        </div>
                        <p className="text-lg text-slate-200 leading-relaxed mb-4">
                          {step.description}
                        </p>
                        {/* Additional details based on step */}
                        {step.id === 1 && (
                          <div className="bg-teal-900/30 border border-teal-700 p-4 rounded-lg">
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-teal-300">{t('processSteps.step1Duration')}</strong> {t('processSteps.step1DurationValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-teal-300">{t('processSteps.step1Format')}</strong> {t('processSteps.step1FormatValue')}</p>
                            <p className="text-sm text-slate-200"><strong className="text-teal-400">{t('processSteps.step1Free')}</strong></p>
                          </div>
                        )}
                        {step.id === 2 && (
                          <div className="bg-orange-900/30 border border-orange-700 p-4 rounded-lg">
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-orange-300">{t('processSteps.step2Delay')}</strong> {t('processSteps.step2DelayValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-orange-300">{t('processSteps.step2Content')}</strong> {t('processSteps.step2ContentValue')}</p>
                            <p className="text-sm text-slate-200"><strong className="text-orange-300">{t('processSteps.step2Validity')}</strong> {t('processSteps.step2ValidityValue')}</p>
                          </div>
                        )}
                        {step.id === 3 && (
                          <div className="bg-blue-900/30 border border-blue-700 p-4 rounded-lg">
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-blue-300">{t('processSteps.step3Preparation')}</strong> {t('processSteps.step3PreparationValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-blue-300">{t('processSteps.step3Materials')}</strong> {t('processSteps.step3MaterialsValue')}</p>
                            <p className="text-sm text-slate-200"><strong className="text-blue-300">{t('processSteps.step3Coordination')}</strong> {t('processSteps.step3CoordinationValue')}</p>
                          </div>
                        )}
                        {step.id === 4 && (
                          <div className="bg-amber-900/30 border border-amber-700 p-4 rounded-lg">
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-amber-300">{t('processSteps.step4Duration')}</strong> {t('processSteps.step4DurationValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-amber-300">{t('processSteps.step4Thickness')}</strong> {t('processSteps.step4ThicknessValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-amber-300">{t('processSteps.step4Reinforcement')}</strong> {t('processSteps.step4ReinforcementValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-amber-300">{t('processSteps.step4Steps')}</strong> {t('processSteps.step4StepsValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-amber-300">{t('processSteps.step4Patterns')}</strong> {t('processSteps.step4PatternsValue')}</p>
                            <p className="text-sm text-slate-200"><strong className="text-amber-300">{t('processSteps.step4Colors')}</strong> {t('processSteps.step4ColorsValue')}</p>
                          </div>
                        )}
                        {step.id === 5 && (
                          <div className="bg-green-900/30 border border-green-700 p-4 rounded-lg">
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-green-300">{t('processSteps.step5Treatment')}</strong> {t('processSteps.step5TreatmentValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-green-300">{t('processSteps.step5Drying')}</strong> {t('processSteps.step5DryingValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-green-300">{t('processSteps.step5Cure')}</strong> {t('processSteps.step5CureValue')}</p>
                            <p className="text-sm text-slate-200"><strong className="text-green-300">{t('processSteps.step5Control')}</strong> {t('processSteps.step5ControlValue')}</p>
                          </div>
                        )}
                        {step.id === 6 && (
                          <div className="bg-purple-900/30 border border-purple-700 p-4 rounded-lg">
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-purple-300">{t('processSteps.step6Advice')}</strong> {t('processSteps.step6AdviceValue')}</p>
                            <p className="text-sm text-slate-200 mb-2"><strong className="text-purple-300">{t('processSteps.step6Availability')}</strong> {t('processSteps.step6AvailabilityValue')}</p>
                            <p className="text-sm text-slate-200"><strong className="text-purple-300">{t('processSteps.step6Warranty')}</strong> {t('processSteps.step6WarrantyValue')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </section>

          {/* Délais et Informations */}
          <section className="mt-12">
            <h2 className="text-3xl font-bold text-white mb-6">{t('howItWorks.delaysTitle')}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-slate-900 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">{t('howItWorks.projectDuration')}</h3>
                  <ul className="space-y-2 text-slate-200">
                    <li>• {t('howItWorks.smallProject')}</li>
                    <li>• {t('howItWorks.mediumProject')}</li>
                    <li>• {t('howItWorks.largeProject')}</li>
                  </ul>
                  <p className="text-sm text-slate-300 mt-4">
                    {t('howItWorks.delaysNote')}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">{t('howItWorks.weatherTitle')}</h3>
                  <p className="text-slate-200 mb-3">
                    {t('howItWorks.weatherDesc')}
                  </p>
                  <ul className="space-y-2 text-slate-200">
                    <li>• {t('howItWorks.temperature')}</li>
                    <li>• {t('howItWorks.noRain')}</li>
                    <li>• {t('howItWorks.moderateWind')}</li>
                  </ul>
                  <p className="text-sm text-slate-300 mt-4">
                    {t('howItWorks.weatherNote')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* CTA */}
          <section className="mt-12">
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">{t('howItWorks.ctaTitle')}</h3>
                <p className="text-slate-300 mb-6">
                  {t('howItWorks.ctaDesc')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                      {t('howItWorks.freeQuote')}
                    </Button>
                  </Link>
                  <Link to="/rdv">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                      {t('howItWorks.bookAppointment')}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;